import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Box, CalendarDays, FileText, Receipt, Sparkles, Truck, Users, Check, ShieldCheck, Tablet, Camera, PenLine, Wallet, Gift } from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { Logo } from "../components/Shared";
import { PricingSection } from "../components/PricingSection";
import { LangSwitch } from "../components/LangSwitch";

const FEATURES = [
  { icon: Sparkles, title: "Estimation IA du volume", text: "Collez l'inventaire du client en texte libre : l'IA détaille chaque meuble, calcule le m³, le camion et l'équipe.", big: true },
  { icon: FileText, title: "Devis conformes", text: "Formules éco, standard, luxe, options monte-meuble, emballage, assurance. TVA 20 % calculée." },
  { icon: Users, title: "CRM déménagement", text: "Pipeline de la demande au déménagement effectué, relances et historique." },
  { icon: CalendarDays, title: "Planning intelligent", text: "Calendrier des chantiers, affectation équipes et véhicules, détection des conflits." },
  { icon: Truck, title: "Flotte & équipes", text: "Camions, monte-meubles, entretien, disponibilité des déménageurs." },
  { icon: Receipt, title: "Facturation", text: "Devis → facture en un clic, acompte 30 %, suivi des règlements et retards." },
];

function VolumeSimulator() {
  const [rooms, setRooms] = useState([3]);
  const volume = rooms[0] * 10 + 5;
  const truck = volume <= 12 ? "Fourgon 12 m³" : volume <= 20 ? "Camion 20 m³" : volume <= 30 ? "Camion 30 m³" : "Semi 50 m³";
  return (
    <div className="rounded-2xl border border-white/60 bg-white/90 p-6 shadow-2xl shadow-blue-900/20 backdrop-blur-xl" data-testid="landing-volume-simulator">
      <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">Simulateur rapide</p>
      <p className="mt-2 text-sm text-slate-600">Nombre de pièces : <span className="font-bold text-slate-900">{rooms[0]}</span></p>
      <Slider data-testid="simulator-rooms-slider" className="my-5" value={rooms} onValueChange={setRooms} min={1} max={8} step={1} />
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-xs text-slate-500">Volume estimé</p>
          <p className="font-mono-num text-2xl font-bold text-[#0b2c6b]" data-testid="simulator-volume">~{volume} m³</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-4">
          <p className="text-xs text-slate-500">Véhicule conseillé</p>
          <p className="text-sm font-bold text-[#0b2c6b]" data-testid="simulator-truck">{truck}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-slate-400">Estimation indicative. L'IA Logidem affine à partir de l'inventaire réel.</p>
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/80 backdrop-blur-lg">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:h-20 lg:px-8">
          <div className="min-w-0 shrink [&_span.font-display]:hidden min-[420px]:[&_span.font-display]:inline"><Logo /></div>
          <nav className="hidden gap-8 text-sm font-medium text-slate-600 md:flex">
            <a href="#fonctionnalites" className="hover:text-slate-900">Fonctionnalités</a>
            <a href="#ia" className="hover:text-slate-900">IA</a>
            <a href="#nomade" className="hover:text-slate-900">Nomade</a>
            <a href="#tarifs" className="hover:text-slate-900">Tarifs</a>
          </nav>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            <LangSwitch />
            <Button asChild variant="ghost" className="px-2 sm:px-4" data-testid="landing-login-button"><Link to="/login">Connexion</Link></Button>
            <Button asChild className="rounded-full bg-[#0b2c6b] px-3 hover:bg-[#1559d6] sm:px-4" data-testid="landing-signup-button"><Link to="/inscription">Essai gratuit</Link></Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden grid-bg">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-24">
          <div className="fade-up lg:col-span-7">
            <span className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-700">
              <Sparkles className="h-3.5 w-3.5" /> SaaS cloud · France & Europe
            </span>
            <h1 className="mt-6 text-4xl font-bold leading-[1.05] text-slate-900 sm:text-5xl lg:text-6xl">
              Le logiciel intelligent <br className="hidden sm:block" />
              <span className="brand-text">des déménageurs.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base text-slate-600 md:text-lg">
              Devis, calcul de volume m³ par IA, CRM, planning, flotte et facturation : toute votre activité de déménagement dans un seul outil, accessible partout.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="rounded-full bg-[#0b2c6b] px-7 hover:bg-[#1559d6]" data-testid="hero-cta-signup">
                <Link to="/inscription">Créer mon compte entreprise <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full px-7" data-testid="hero-cta-login">
                <Link to="/login">Accéder à mon espace</Link>
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-500">
              {["TVA & mentions françaises", "Hébergé en cloud", "Multi-entreprises"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="h-4 w-4 text-teal-500" />{t}</span>
              ))}
            </div>
          </div>
          <div className="relative fade-up lg:col-span-5" style={{ animationDelay: "0.15s" }}>
            <img src="/images/hero-truck.jpg" alt="Camion de déménagement" className="h-[460px] w-full rounded-3xl object-cover" />
            <div className="absolute -bottom-8 -left-4 right-6 sm:-left-10 sm:right-auto sm:w-80">
              <VolumeSimulator />
            </div>
          </div>
        </div>
      </section>

      <section id="fonctionnalites" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">Tout-en-un</p>
        <h2 className="mt-2 max-w-2xl text-3xl font-bold text-slate-900 sm:text-4xl">Pensé pour le métier du déménagement, de la visite technique à l'encaissement.</h2>
        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, text, big }) => (
            <div key={title} className={`card-hover rounded-2xl border border-slate-200 p-6 ${big ? "brand-gradient text-white md:row-span-2" : "bg-white"}`}>
              <div className={`mb-4 inline-flex h-10 w-10 items-center justify-center rounded-xl ${big ? "bg-white/20" : "bg-slate-100 text-[#0b2c6b]"}`}><Icon className="h-5 w-5" /></div>
              <h3 className={`text-lg font-semibold ${big ? "text-white" : "text-slate-900"}`}>{title}</h3>
              <p className={`mt-2 text-sm ${big ? "text-white/85" : "text-slate-600"}`}>{text}</p>
              {big && (
                <div className="mt-6 rounded-xl bg-white/10 p-4 font-mono-num text-xs leading-relaxed text-white/90">
                  « 3 chambres, canapé d'angle, frigo américain, 25 cartons, piano droit »<br />→ 38,6 m³ · Camion 50 m³ · 4 déménageurs
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section id="ia" className="bg-[#0b2c6b]">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-24 sm:px-6 lg:grid-cols-2 lg:px-8">
          <img src="/images/team.jpg" alt="Équipe de déménageurs" className="h-80 w-full rounded-3xl object-cover" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-300">Assistant commercial IA</p>
            <h2 className="mt-2 text-3xl font-bold text-white sm:text-4xl">Un commercial expert, disponible 24h/24.</h2>
            <p className="mt-4 text-white/75">Relances de devis, réponses aux objections prix, calcul de surcoûts piano ou monte-meuble : l'assistant connaît vos tarifs et vos devis en cours.</p>
            <ul className="mt-6 space-y-3 text-sm text-white/85">
              {["Rédiger un e-mail de relance personnalisé", "Répondre à « c'est trop cher »", "Préparer une visite technique"].map((t) => (
                <li key={t} className="flex items-center gap-3"><Sparkles className="h-4 w-4 text-teal-300" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="nomade" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-teal-600">Application Nomade</p>
            <h2 className="mt-2 text-3xl font-bold text-slate-900 sm:text-4xl">Vos équipes terrain connectées, chantier par chantier.</h2>
            <p className="mt-4 text-slate-600">Sur tablette ou smartphone, chaque déménageur retrouve son planning et documente le chantier en temps réel. Le bureau voit tout instantanément.</p>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {[[CalendarDays, "Planning jour & semaine"], [PenLine, "Signatures client sur tablette"], [Camera, "Photos avant / après / dommages"], [FileText, "Comptes rendus de déménagement"], [Wallet, "Dépenses du jour et justificatifs"], [Tablet, "Optimisé tablette 7 pouces"]].map(([Icon, t]) => (
                <div key={t} className="flex items-center gap-3 text-sm font-medium text-slate-700"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-teal-50 text-teal-600"><Icon className="h-4 w-4" /></span>{t}</div>
              ))}
            </div>
          </div>
          <div className="brand-gradient rounded-3xl p-8 text-white" data-testid="launch-offer">
            <Gift className="h-8 w-8" />
            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-white/80">Offre de lancement</p>
            <h3 className="mt-2 font-display text-3xl font-bold">Une tablette Nomade 7 pouces offerte</h3>
            <p className="mt-3 text-white/85">Pour chaque nouvelle installation et souscription d'abonnement LOGIDEM, nous vous offrons une tablette 7 pouces prête à l'emploi pour vos équipes terrain.</p>
            <Button asChild className="mt-6 rounded-full bg-white text-[#0b2c6b] hover:bg-white/90" data-testid="launch-offer-cta"><Link to="/inscription">Profiter de l'offre</Link></Button>
          </div>
        </div>
      </section>

      <PricingSection />

      <footer className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-10 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <Logo testid="footer-logo" />
          <p className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Données hébergées dans le cloud · RGPD</p>
          <p>© {new Date().getFullYear()} Logidem. <Box className="inline h-3 w-3" /> Le logiciel intelligent des déménageurs.</p>
        </div>
      </footer>
    </div>
  );
}
